/* línkè · Ombre Brain 数据层 =====================================
   - 真实后端接口先尝试，失败回退 mock。
   - 适配层 normalizeBucket 兼容多种字段命名（接口字段确认后只改这一处）。
   - 暴露 LIVE 状态，让 UI 显示「已连接 / 示例数据」徽标。
   ================================================================ */
import { MOCK_BUCKETS, MOCK_STATUS } from './mock.js';

const API_BASE = ''; // 同源部署，相对路径即可

let LIVE = false;
export function isLive() {
  return LIVE;
}

async function api(path, options) {
  const res = await fetch(API_BASE + path, {
    headers: { Accept: 'application/json', ...(options?.headers || {}) },
    credentials: 'include', // 把 ombre_session cookie 带上
    ...options,
  });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  const text = await res.text();
  // 防御：如果 nginx location 没配对，可能拿到 "lingke.bond is alive" 这种纯文本
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error('Non-JSON response: ' + text.slice(0, 64));
  }
}

const num = (v, d) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
// 把任意输入归一化成 array of non-empty string
// 防御：原数据可能是 array of [null, 0, "love"] 这种异常格式（yaml 解析时 tags: 空行会变成 [null]）
const arr = (v) => {
  if (Array.isArray(v)) {
    return v
      .map((x) => (x == null ? '' : String(x).trim()))
      .filter(Boolean);
  }
  if (typeof v === 'string' && v) {
    return v.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

/** 把后端返回的一条记录整理成前端统一结构。接口字段不一致时只改这里。
 *  2026-05-25 实测真实后端字段差异：
 *  - id / name（不是 bucket_id / title）— 已兼容
 *  - score（不是 weight）— 新加
 *  - content_preview（不是 content）— 新加
 *  - type: "permanent"（不是 "solid"）— 映射到前端期望的 "solid"
 *  - created（不是 created_at）— 已兼容
 */
export function normalizeBucket(r) {
  if (!r || typeof r !== 'object') return null;
  // type 映射：后端 permanent ↔ 前端 solid（前端用 type === 'solid' 判断"固化记忆"）
  let type = r.type ?? r.bucket_type ?? 'dynamic';
  if (type === 'permanent') type = 'solid';
  return {
    bucket_id: r.bucket_id ?? r.id ?? r.bucketId ?? '',
    title: r.title ?? r.name ?? r.subject ?? '未命名记忆',
    domain: arr(r.domain ?? r.topic ?? r.theme),
    valence: num(r.valence ?? r.v, 0.5),
    arousal: num(r.arousal ?? r.a, 0.5),
    importance: num(r.importance ?? r.imp, 5),
    // 真实后端字段是 score（衰减权重），前端继续叫 weight
    weight: num(r.weight ?? r.w ?? r.score, 1),
    pinned: !!(r.pinned ?? r.pin),
    type,
    tags: arr(r.tags),
    // 真实后端的 /api/buckets 列表里只返回 content_preview（截断版）；
    // /api/bucket/{id} 详情才有完整 content。两者都兼容。
    content: r.content ?? r.text ?? r.body ?? r.content_preview ?? '',
    created_at:
      r.created_at ?? r.createdAt ?? r.created ?? r.timestamp ?? null,
  };
}

export async function loadBuckets() {
  try {
    const data = await api('/api/buckets');
    const raw = Array.isArray(data)
      ? data
      : data.buckets || data.items || data.data || [];
    const list = raw.map(normalizeBucket).filter(Boolean);
    if (!list.length) throw new Error('empty');
    LIVE = true;
    return list;
  } catch (e) {
    LIVE = false;
    return MOCK_BUCKETS.map(normalizeBucket);
  }
}

export async function loadStatus() {
  try {
    const s = await api('/api/status');
    return {
      solid: s.solid ?? s.solid_count ?? s.fixed ?? null,
      dynamic: s.dynamic ?? s.dynamic_count ?? null,
      archived: s.archived ?? s.archive_count ?? 0,
      size: s.size ?? s.total_size ?? null,
      engine: s.engine_running ?? s.decay ?? true,
    };
  } catch (e) {
    return {
      solid: MOCK_STATUS.solid,
      dynamic: MOCK_STATUS.dynamic,
      archived: MOCK_STATUS.archived,
      size: MOCK_STATUS.size,
      engine: MOCK_STATUS.engine_running,
    };
  }
}

export async function loadDetail(id, cache) {
  const cached = cache?.find((b) => b.bucket_id === id);
  try {
    const d = await api('/api/bucket/' + encodeURIComponent(id));
    // 详情接口把心情/重要度/日期都放在 metadata 里，摊平再读（不然会被当成默认值，盖掉列表里的真值）
    const raw = d.bucket || d.data || d;
    const detailed = normalizeBucket(raw && raw.metadata ? { ...raw.metadata, ...raw } : raw);
    // 详情接口字段可能跟列表不完全一致 —— 详情优先，缺失时回退列表里的兜底
    // 特别是 title：列表的 name 字段一定有，详情若返回空就用列表的
    if (cached) {
      return {
        ...cached,
        ...detailed,
        title:
          detailed.title && detailed.title !== '未命名记忆'
            ? detailed.title
            : cached.title,
        content: detailed.content || cached.content,
        tags: detailed.tags?.length ? detailed.tags : cached.tags,
        domain: detailed.domain?.length ? detailed.domain : cached.domain,
      };
    }
    return detailed;
  } catch (e) {
    return cached || null;
  }
}

/* 记忆页搜索（10-16 铃：「经常搜不到想找的」）。
   原来走大脑 /api/search：只回 10 条、不搜归档、先用向量粗筛 50 条再模糊打分 → 实测「绒绒」
   真写到的 22 篇只出 3 篇、「驾照」8 篇出 0 篇。现在改成：
   ① 先在已经拿到手的全部记忆（含归档、含全文）里按字面找——空格隔开的几个词要都出现；
      标题里有 > 标签里有 > 正文里出现得多，同分新的在前；
   ② 字面找到的少于 5 篇，再去大脑要「意思相近」的补在后面（标 near，页面上单独一段）。 */
export function matchLocal(q, cache) {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const out = [];
  for (const b of cache || []) {
    const title = b.title.toLowerCase();
    const tags = [...b.tags, ...b.domain].join(' ').toLowerCase();
    const body = (b.content || '').toLowerCase();
    let score = 0;
    let ok = true;
    for (const t of terms) {
      const inT = title.includes(t);
      const inG = tags.includes(t);
      const n = body.split(t).length - 1;
      if (!inT && !inG && !n) {
        ok = false;
        break;
      }
      score += (inT ? 30 : 0) + (inG ? 20 : 0) + Math.min(n, 10) * 2;
    }
    if (ok) out.push({ b, score });
  }
  out.sort(
    (x, y) => y.score - x.score || String(y.b.created_at || '').localeCompare(String(x.b.created_at || '')),
  );
  return out.map((x) => x.b);
}

export async function searchBuckets(qstr, cache) {
  const q = (qstr || '').trim();
  if (!q) return cache || [];
  const exact = matchLocal(q, cache);
  if (!LIVE || exact.length >= 5) return exact;
  try {
    const data = await api('/api/search?q=' + encodeURIComponent(q));
    const raw = Array.isArray(data) ? data : data.results || data.buckets || [];
    const seen = new Set(exact.map((b) => b.bucket_id));
    const near = raw
      .map(normalizeBucket)
      .filter((r) => r && !seen.has(r.bucket_id))
      // 用手里那份（带日期/全文/恒星标记），手里没有的才用搜索结果那份
      .map((r) => ({ ...((cache || []).find((x) => x.bucket_id === r.bucket_id) || r), near: true }));
    return [...exact, ...near];
  } catch (e) {
    return exact;
  }
}

/**
 * 写入一条新记忆（dynamic 桶）。后端实现见 server.py 的 /api/bucket POST 路由，
 * 复用 write_memory.py 的写入函数。
 * @param {Object} payload  {name, content, domain?, tags?, valence?, arousal?, importance?}
 * @returns {Promise<{ok, bucket_id, id}>}
 */
export async function createBucket(payload) {
  const res = await fetch(API_BASE + '/api/bucket', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error('后端返回非 JSON：' + text.slice(0, 120));
  }
  if (!res.ok) {
    const msg = data.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

/**
 * 删除一条记忆（2026-07-10 铃要的删除按钮）。
 * 后端 DELETE /api/bucket/{id}：protected 桶会被拒（403），删除连带清 embedding。
 * @returns {Promise<{ok, id}>}
 */
export async function deleteBucket(id) {
  const res = await fetch(API_BASE + '/api/bucket/' + encodeURIComponent(id), {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error('后端返回非 JSON：' + text.slice(0, 120));
  }
  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

/** @deprecated 用 createBucket */
export const writeBucket = createBucket;
